import type { CSSProperties } from "react";

import type { Survivor } from "../types";
import type { SurvivorMotion, SurvivorRigProp } from "../lib/survivorMotion";
import { resolvedAppearance, safeArtToken } from "./survivorArt";
import {
  SURVIVOR_RIG_BONES,
  SURVIVOR_RIG_VERSION,
  type SurvivorRigCell,
} from "./survivorRigManifest";

interface SurvivorRigProps {
  survivor: Survivor;
  atlas: string;
  motion: SurvivorMotion;
  prop: SurvivorRigProp;
  facing?: 1 | -1;
  injury?: number;
  className?: string;
  decorative?: boolean;
  onAtlasError?: () => void;
  lod?: "full" | "compact";
}

function RigSprite({ cell, className = "" }: { cell: SurvivorRigCell; className?: string }) {
  return <i className={`rig-sprite atlas-${cell} ${className}`.trim()} aria-hidden="true" />;
}

function RigProp({ prop }: { prop: SurvivorRigProp }) {
  if (prop === "none") return null;
  return (
    <span className={`rig-prop rig-prop-${prop}`} aria-hidden="true">
      <RigSprite cell={prop} />
    </span>
  );
}

export function SurvivorRig({
  survivor,
  atlas,
  motion,
  prop,
  facing = 1,
  injury = 0,
  className = "",
  decorative = true,
  onAtlasError,
  lod = "full",
}: SurvivorRigProps) {
  const appearance = resolvedAppearance(survivor);
  const phase = Math.max(0, Math.min(1, appearance.motion_phase / 1000));
  const style = {
    "--rig-atlas": `url("${atlas}")`,
    "--rig-facing": facing,
    "--rig-injury": Math.max(0, Math.min(1, injury)),
    "--phase-idle": `${-(phase * 6.4)}s`,
    "--phase-walk": `${-(phase * 1.08)}s`,
    "--phase-climb": `${-(phase * 1.35)}s`,
    "--phase-labour": `${-(phase * 1.72)}s`,
    "--phase-precision": `${-(phase * 4.8)}s`,
    "--phase-social": `${-(phase * 5.6)}s`,
    "--phase-guard": `${-(phase * 6.8)}s`,
    "--phase-rest": `${-(phase * 7.2)}s`,
  } as CSSProperties;
  const accessibility = decorative
    ? { "aria-hidden": true as const }
    : { role: "img", "aria-label": `${survivor.name}, animated full figure` };

  return (
    <span
      className={`survivor-rig rig-lod-${lod} body-${safeArtToken(appearance.recipe.body)} motion-${motion} ${injury > 0 ? "is-injured" : ""} ${className}`.trim()}
      data-animation-budget={lod === "compact" ? "root-only" : "full"}
      data-motion={motion}
      data-rig-lod={lod}
      data-rig-bone-count={SURVIVOR_RIG_BONES.length}
      data-rig-profile={appearance.profile_id ?? "procedural"}
      data-rig-prop={prop}
      data-rig-version={SURVIVOR_RIG_VERSION}
      style={style}
      {...accessibility}
    >
      <img
        alt=""
        className="rig-atlas-probe"
        decoding="async"
        draggable={false}
        loading="eager"
        onError={onAtlasError}
        src={atlas}
      />
      <span className="rig-ground-shadow" aria-hidden="true" />
      <span className="rig-skeleton" aria-hidden="true">
        <span className="rig-puppet">
        <span className="rig-bone bone-leg-back bone-leg-back-upper">
          <RigSprite cell="thigh-left" className="part-thigh part-thigh-back" />
          <span className="rig-bone bone-leg-back-lower">
            <RigSprite cell="shin-left" className="part-shin part-shin-back" />
          </span>
        </span>
        <span className="rig-bone bone-torso">
          <span className="rig-bone bone-arm-back bone-arm-back-upper">
            <RigSprite cell="upper-left" className="part-upper-arm part-upper-arm-back" />
            <span className="rig-bone bone-arm-back-lower">
              <RigSprite cell="lower-left" className="part-lower-arm part-lower-arm-back" />
            </span>
          </span>
          <RigSprite cell="torso" className="part-torso" />
          <span className="rig-bone bone-head">
            <RigSprite cell="head" className="part-head" />
          </span>
          <span className="rig-bone bone-bag">
            <RigSprite cell="bag" className="part-bag" />
          </span>
          <span className="rig-bone bone-arm-front bone-arm-front-upper">
            <RigSprite cell="upper-right" className="part-upper-arm part-upper-arm-front" />
            <span className="rig-bone bone-arm-front-lower">
              <RigSprite cell="lower-right" className="part-lower-arm part-lower-arm-front" />
              <RigProp prop={prop} />
            </span>
          </span>
        </span>
        <span className="rig-bone bone-pelvis">
          <RigSprite cell="pelvis" className="part-pelvis" />
        </span>
        <span className="rig-bone bone-leg-front bone-leg-front-upper">
          <RigSprite cell="thigh-right" className="part-thigh part-thigh-front" />
          <span className="rig-bone bone-leg-front-lower">
            <RigSprite cell="shin-right" className="part-shin part-shin-front" />
          </span>
        </span>
        <span className="rig-wound rig-wound-head" />
        <span className="rig-wound rig-wound-torso" />
        <span className="rig-wound rig-wound-leg" />
        </span>
      </span>
    </span>
  );
}
